import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import styled, { keyframes } from "styled-components";

gsap.registerPlugin(ScrollTrigger);

const categories = [
  {
    id: 1,
    name: "Laptops",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
        <line x1="2" y1="20" x2="22" y2="20" />
      </svg>
    ),
    color: "#3b82f6",
  },
  {
    id: 2,
    name: "Printers",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <polyline points="6 9 6 2 18 2 18 9" />
        <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
        <rect x="6" y="14" width="12" height="8" />
      </svg>
    ),
    color: "#d93a35",
  },
  {
    id: 3,
    name: "Desktops",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
        <line x1="8" y1="21" x2="16" y2="21" />
        <line x1="12" y1="17" x2="12" y2="21" />
      </svg>
    ),
    color: "#10b981",
  },
  {
    id: 4,
    name: "Toners",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M8 2h8l2 4H6l2-4z" />
        <rect x="6" y="6" width="12" height="14" rx="1" />
        <line x1="10" y1="10" x2="14" y2="10" />
      </svg>
    ),
    color: "#8b5cf6",
  },
  {
    id: 5,
    name: "Accessories",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <rect x="4" y="4" width="16" height="16" rx="2" />
        <line x1="9" y1="9" x2="15" y2="15" />
        <line x1="15" y1="9" x2="9" y2="15" />
      </svg>
    ),
    color: "#f59e0b",
  },
  {
    id: 6,
    name: "Networking",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <circle cx="12" cy="12" r="2" />
        <path d="M16.24 7.76a6 6 0 0 1 0 8.49m-8.48-.01a6 6 0 0 1 0-8.49m11.31-2.82a10 10 0 0 1 0 14.14m-14.14 0a10 10 0 0 1 0-14.14" />
      </svg>
    ),
    color: "#ec4899",
  },
];

const ProductCategories = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        section.querySelectorAll(".category-card"),
        { opacity: 0, y: 30, scale: 0.9 },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.5,
          stagger: 0.1,
          ease: "back.out(1.7)",
          scrollTrigger: {
            trigger: section,
            start: "top 85%",
            toggleActions: "play none none reverse",
          },
        }
      );
    }, section);

    return () => ctx.revert();
  }, []);

  const handleCategoryClick = (categoryName: string) => {
    navigate(`/products?category=${encodeURIComponent(categoryName)}`);
  };

  return (
    <CategoriesSection ref={sectionRef}>
      <div className="container">
        <div className="section-header">
          <h2>Browse by Category</h2>
          <p>Find exactly what you need</p>
        </div>

        <div className="categories-grid">
          {categories.map((category) => (
            <div
              key={category.id}
              className="category-card"
              style={{ "--category-color": category.color } as React.CSSProperties}
              onClick={() => handleCategoryClick(category.name)}
            >
              <div className="icon-wrapper">{category.icon}</div>
              <span className="category-name">{category.name}</span>
              <div className="hover-bg" />
            </div>
          ))}
        </div>
      </div>
    </CategoriesSection>
  );
};

export default ProductCategories;

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-5px); }
`;

const CategoriesSection = styled.section`
  background: var(--bg-primary);
  padding: 80px 20px;
  position: relative;

  .container {
    max-width: 1200px;
    margin: 0 auto;
  }

  .section-header {
    text-align: center;
    margin-bottom: 50px;

    h2 {
      font-family: Poppins;
      font-size: 36px;
      font-weight: 800;
      color: var(--text-primary);
      margin: 0 0 10px;
    }

    p {
      font-family: Poppins;
      font-size: 16px;
      color: var(--text-secondary);
      margin: 0;
    }
  }

  .categories-grid {
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    gap: 20px;
  }

  .category-card {
    position: relative;
    background: var(--card-bg);
    border: 2px solid var(--card-border);
    border-radius: 20px;
    padding: 30px 20px;
    text-align: center;
    cursor: pointer;
    transition: all 0.4s ease;
    overflow: hidden;

    &:hover {
      border-color: var(--category-color);
      transform: translateY(-8px);
      box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);

      .icon-wrapper {
        animation: ${float} 1s ease-in-out infinite;
        background: var(--category-color);

        svg {
          stroke: #fff;
        }
      }

      .category-name {
        color: var(--category-color);
      }

      .hover-bg {
        opacity: 0.05;
        transform: scale(1);
      }
    }

    .icon-wrapper {
      width: 70px;
      height: 70px;
      margin: 0 auto 16px;
      background: linear-gradient(135deg, rgba(217, 58, 53, 0.1) 0%, rgba(43, 59, 124, 0.1) 100%);
      border-radius: 16px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.4s ease;

      svg {
        width: 32px;
        height: 32px;
        stroke: var(--category-color);
        transition: all 0.3s ease;
      }
    }

    .category-name {
      font-family: Poppins;
      font-size: 15px;
      font-weight: 600;
      color: var(--text-primary);
      transition: color 0.3s ease;
    }

    .hover-bg {
      position: absolute;
      top: 50%;
      left: 50%;
      width: 200%;
      height: 200%;
      background: var(--category-color);
      border-radius: 50%;
      transform: translate(-50%, -50%) scale(0);
      opacity: 0;
      transition: all 0.5s ease;
      z-index: 0;
    }

    .icon-wrapper,
    .category-name {
      position: relative;
      z-index: 1;
    }
  }

  @media (max-width: 1024px) {
    .categories-grid {
      grid-template-columns: repeat(3, 1fr);
    }
  }

  @media (max-width: 640px) {
    padding: 60px 20px;

    .section-header h2 {
      font-size: 28px;
    }

    .categories-grid {
      grid-template-columns: repeat(2, 1fr);
      gap: 15px;
    }

    .category-card {
      padding: 24px 16px;

      .icon-wrapper {
        width: 56px;
        height: 56px;

        svg {
          width: 26px;
          height: 26px;
        }
      }

      .category-name {
        font-size: 13px;
      }
    }
  }
`;
